// src/pages/MyProfile/components/SettingContent.jsx
import React, { useState } from "react";
import {
    Typography,
    Box,
    TextField,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    InputAdornment,
    Snackbar,
    Alert,
    CircularProgress // Import CircularProgress for loading indicator
} from "@mui/material";

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// Import Firebase Auth
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { auth } from "../../../firebase"; // Asumsi path ke firebase.js Anda

const SettingContent = () => {
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [loading, setLoading] = useState(false); // State for loading indicator

    const handleClickShowCurrentPassword = () => setShowCurrentPassword((show) => !show);
    const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
    const handleClickShowConfirmNewPassword = () => setShowConfirmNewPassword((show) => !show);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const validatePassword = (password) => {
        const errors = [];
        if (password.length < 8) {
            errors.push("Minimal 8 karakter");
        }
        if (!/[a-z]/.test(password) || !/[A-Z]/.test(password)) {
            errors.push("Setidaknya satu huruf kecil dan satu huruf besar");
        }
        if (!/\d/.test(password)) {
            errors.push("Setidaknya satu angka");
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            errors.push("Setidaknya satu simbol atau karakter khusus");
        }
        return errors;
    };

    const handleSavePassword = async () => {
        setLoading(true); // Start loading

        const newPasswordErrors = validatePassword(newPassword);

        if (newPasswordErrors.length > 0) {
            setSnackbar({
                open: true,
                message: `Kata sandi baru tidak memenuhi persyaratan: ${newPasswordErrors.join(', ')}.`,
                severity: "error"
            });
            setLoading(false); // Stop loading
            return;
        }

        if (newPassword !== confirmNewPassword) {
            setSnackbar({
                open: true,
                message: "Konfirmasi kata sandi baru tidak cocok dengan kata sandi baru.",
                severity: "error"
            });
            setLoading(false); // Stop loading
            return;
        }

        const user = auth.currentUser;

        if (user) {
            // Re-authenticate user
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            try {
                await reauthenticateWithCredential(user, credential);
                // Password re-authenticated, now update the password
                await updatePassword(user, newPassword);

                setSnackbar({
                    open: true,
                    message: "Kata sandi berhasil diubah!",
                    severity: "success"
                });

                // Reset fields
                setCurrentPassword('');
                setNewPassword('');
                setConfirmNewPassword('');
                setShowCurrentPassword(false);
                setShowNewPassword(false);
                setShowConfirmNewPassword(false);

            } catch (error) {
                console.error("Error updating password:", error);
                let errorMessage = "Gagal mengubah kata sandi.";
                if (error.code === 'auth/wrong-password') {
                    errorMessage = "Kata sandi saat ini salah.";
                } else if (error.code === 'auth/requires-recent-login') {
                    errorMessage = "Sesi Anda sudah habis. Mohon login kembali untuk mengubah kata sandi.";
                } else if (error.code === 'auth/weak-password') {
                    errorMessage = "Kata sandi terlalu lemah. Gunakan kombinasi yang lebih kuat.";
                }
                setSnackbar({
                    open: true,
                    message: errorMessage,
                    severity: "error"
                });
            } finally {
                setLoading(false); // Stop loading regardless of success or failure
            }
        } else {
            setSnackbar({
                open: true,
                message: "Tidak ada pengguna yang login.",
                severity: "error"
            });
            setLoading(false); // Stop loading
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Headline untuk bagian Ubah Kata Sandi */}
            <Typography variant="h6" gutterBottom>
                Ubah Kata Sandi
            </Typography>

            {/* Container untuk "EditText Kata Sandi" dan "Persyaratan Kata Sandi" */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 4 }}>
                {/* Area EditText Kata Sandi (Kiri) */}
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Kata Sandi Saat Ini"
                            type={showCurrentPassword ? 'text' : 'password'}
                            fullWidth
                            variant="outlined"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowCurrentPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            disabled={loading} // Disable while loading
                                        >
                                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            disabled={loading} // Disable while loading
                        />
                        <TextField
                            label="Kata Sandi Baru"
                            type={showNewPassword ? 'text' : 'password'}
                            fullWidth
                            variant="outlined"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowNewPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            disabled={loading} // Disable while loading
                                        >
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            disabled={loading} // Disable while loading
                        />
                        <TextField
                            label="Konfirmasi Kata Sandi Baru"
                            type={showConfirmNewPassword ? 'text' : 'password'}
                            fullWidth
                            variant="outlined"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowConfirmNewPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            disabled={loading} // Disable while loading
                                        >
                                            {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            disabled={loading} // Disable while loading
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ mt: 2 }}
                            onClick={handleSavePassword}
                            disabled={loading} // Disable button while loading
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : "Simpan Kata Sandi"}
                        </Button>
                    </Box>
                </Box>

                {/* Area Keterangan Persyaratan Kata Sandi (Kanan) */}
                <Box sx={{ flex: 1, mt: { xs: 4, sm: 0 } }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Persyaratan Kata Sandi:
                    </Typography>
                    <List dense>
                        <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 35 }}>
                                {validatePassword(newPassword).includes("Minimal 8 karakter") ? (
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                ) : (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                )}
                            </ListItemIcon>
                            <ListItemText primary="Minimal 8 karakter - semakin banyak semakin baik" />
                        </ListItem>
                        <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 35 }}>
                                {validatePassword(newPassword).includes("Setidaknya satu huruf kecil dan satu huruf besar") ? (
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                ) : (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                )}
                            </ListItemIcon>
                            <ListItemText primary="Setidaknya satu huruf kecil dan satu huruf besar" />
                        </ListItem>
                        <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 35 }}>
                                {validatePassword(newPassword).includes("Setidaknya satu angka") ? (
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                ) : (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                )}
                            </ListItemIcon>
                            <ListItemText primary="Setidaknya satu angka" />
                        </ListItem>
                        <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 35 }}>
                                {validatePassword(newPassword).includes("Setidaknya satu simbol atau karakter khusus") ? (
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                ) : (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                )}
                            </ListItemIcon>
                            <ListItemText primary="Setidaknya satu simbol atau karakter khusus" />
                        </ListItem>
                    </List>
                </Box>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SettingContent;
